import {ChangeEvent, useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {
  Box,
  Card,
  CardHeader,
  Checkbox,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Tooltip
} from '@mui/material';

interface MapSelectProps {
  mapSeries: string[];
  selectedValues: string[];
  setSelectedValues: (values: string[]) => void;
}

export const MapSelect = (props: MapSelectProps) => {
  const {t} = useTranslation();

  const handleChange = useCallback(
    (event: any, item: string) => {
      event.stopPropagation();
      const newSelectedValues = props.mapSeries.filter(value =>
        props.selectedValues.includes(item)
          ? props.selectedValues.includes(value) && value !== item
          : props.selectedValues.includes(value) || value === item
      );
      props.setSelectedValues(newSelectedValues);
    },
    [props.mapSeries, props.selectedValues, props.setSelectedValues]
  );

  const handleSelectAll = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        props.setSelectedValues(props.mapSeries);
      } else {
        props.setSelectedValues([]);
      }
    },
    [props.mapSeries, props.setSelectedValues]
  );

  const memoizedListItems = useMemo(
    () =>
      props.mapSeries.map((item, index) => (
        <ListItemButton
          key={index}
          role="listitem"
          onClick={event => handleChange(event, item)}
          sx={{
            bgcolor: props.selectedValues.includes(item) ? 'divider' : ''
          }}
        >
          <Tooltip title={item}>
            <ListItemText id={`transfer-item-${index}-label`} primary={item} />
          </Tooltip>
        </ListItemButton>
      )),
    [props.mapSeries, props.selectedValues, handleChange]
  );

  return (
    <Card>
      <Box height="65vh" display="flex" flexDirection="column">
        <CardHeader
          sx={{px: 0, py: 1}}
          avatar={
            <Tooltip title={t('全部位選択')}>
              <Checkbox
                checked={
                  props.mapSeries.length > 0 &&
                  props.selectedValues.length === props.mapSeries.length
                }
                onChange={handleSelectAll}
                color="primary"
              />
            </Tooltip>
          }
          title={t('部位名選択')}
        />
        <Divider />
        <List
          sx={{
            flex: 1,
            bgcolor: 'background.paper',
            overflow: 'auto',
            p: 0
          }}
          dense
          component="div"
          role="list"
        >
          {memoizedListItems}
        </List>
      </Box>
    </Card>
  );
};
